import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Redirect, useHistory } from 'react-router-dom';
import { DefaultTemplate } from 'views/templates/DefaultTemplate';
import { ConfigurationProfilePageLayout } from 'views/layouts/ConfigurationProfilePageLayout';
import {
  useSelector as useReduxSelector,
  TypedUseSelectorHook,
} from 'react-redux';
import { AppState, useAppDispatch } from 'stores/type';
import queryString from 'query-string';
import { Splash } from 'views/components/molecules/Splash';
import { DeviceDomain } from 'models/Device';
import { DistributionDomain } from 'models/Distribution';
import { setExceptionError } from 'stores/errorSlice';
import { localeIds } from 'locals';
import { ROUTER_PATH } from 'views/constants';
import { getRequestPath } from 'utils/string';
import { closeDialog, showDialog } from 'stores/dialogSlice';
import { useDevice } from 'views/hooks/useDevice';

export const ConfigurationProfilePage: React.FC = () => {
  const history = useHistory();
  const { startInstallConfigurationProfile } = useDevice();
  const intl = useIntl();
  const useSelector: TypedUseSelectorHook<AppState> = useReduxSelector;
  const device = useSelector((state) => state.deviceState.device);
  const deviceDomain = useMemo(() => {
    return new DeviceDomain(device);
  }, [device]);
  const [triedDistributionJoin, setTriedDistributionJoin] = useState(false);
  const [profileInstallUrl, setProfileInstallUrl] = useState<
    string | undefined
  >(undefined);
  const [disabledInstallButton, setDisabledInstallButton] = useState(false);
  const dispatch = useAppDispatch();
  const distributionAccessKey = queryString.parse(location.search)[
    'distribution_access_key'
  ] as string;
  const distributionKey = queryString.parse(location.search)['key'] as string;
  const distributionDeviceName = queryString.parse(location.search)[
    'name'
  ] as string;
  const appId = queryString.parse(location.search)['app_id'] as string;

  const handleCloseDialogAndGoBack = useCallback(() => {
    dispatch(closeDialog());
    history.goBack();
  }, [history, dispatch]);

  // ConfigurationProfileを既にインストール済みでdistribution access keyが渡ってきた場合、対象のdistributionへjoinを試みる
  useEffect(() => {
    if (
      deviceDomain.isInstalledConfigurationProfile() &&
      distributionAccessKey
    ) {
      const distributionDomain = new DistributionDomain(
        distributionAccessKey,
        device
      );
      distributionDomain
        .join(distributionKey, distributionDeviceName)
        .then((result) => {
          if (result.isFailure()) {
            dispatch(
              showDialog({
                content: intl.formatMessage({ id: result.value.localeId() }),
                defaultAction: {
                  label: intl.formatMessage({ id: localeIds.dialog.ok }),
                  callback: handleCloseDialogAndGoBack,
                },
              })
            );
            return;
          }

          history.replace(
            getRequestPath(ROUTER_PATH.distribution, { distributionAccessKey })
          );
        })
        .catch((e) => {
          dispatch(setExceptionError(e));
        });
    } else {
      setTriedDistributionJoin(true);
    }
  }, [
    device,
    deviceDomain,
    dispatch,
    distributionAccessKey,
    distributionDeviceName,
    distributionKey,
    handleCloseDialogAndGoBack,
    history,
    intl,
  ]);

  useEffect(() => {
    deviceDomain
      .installConfigurationProfileUrl(
        device?.user?.authorizationToken,
        device?.iphoneRegistrationToken,
        distributionAccessKey,
        distributionKey,
        distributionDeviceName,
        appId,
        deviceDomain.hasReInstall()
      )
      .then((url) => {
        if (url) {
          setProfileInstallUrl(url);
        }
      })
      .catch((e) => {
        dispatch(setExceptionError(e));
      });
  }, [
    appId,
    device?.iphoneRegistrationToken,
    device?.user?.authorizationToken,
    deviceDomain,
    dispatch,
    distributionAccessKey,
    distributionDeviceName,
    distributionKey,
  ]);

  const handleInstall = useCallback(async (): Promise<void> => {
    setDisabledInstallButton(true);

    await startInstallConfigurationProfile()
      .then(() => {
        if (distributionAccessKey) {
          history.push(
            getRequestPath(ROUTER_PATH.distribution, {
              distributionAccessKey,
            })
          );
        } else {
          history.push(ROUTER_PATH.apps);
        }
      })
      .catch((e) => {
        dispatch(setExceptionError(e));
      });
  }, [
    distributionAccessKey,
    startInstallConfigurationProfile,
    dispatch,
    history,
  ]);

  if (
    !deviceDomain.hasIphoneRegistrationToken() &&
    !distributionAccessKey &&
    !deviceDomain.hasReInstall()
  ) {
    return <Redirect to={ROUTER_PATH.login} />;
  } else if (deviceDomain.isInstalledConfigurationProfile()) {
    return <Redirect to={ROUTER_PATH.apps} />;
  } else if (!triedDistributionJoin || !profileInstallUrl) {
    return (
      <DefaultTemplate headerText="">
        <Splash />
      </DefaultTemplate>
    );
  } else {
    return (
      <DefaultTemplate
        headerText={intl.formatMessage({
          id: localeIds.configurationProfilePageLayout.title,
        })}
      >
        <ConfigurationProfilePageLayout
          installUrl={profileInstallUrl}
          onInstall={handleInstall}
          disabledInstallButton={disabledInstallButton}
        />
      </DefaultTemplate>
    );
  }
};
